import React, { useCallback } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Space, UploadImagesArgs } from '@tribeplatform/gql-client/types'
import {
  useCreateEmojis,
  useCreateImages,
  useNetwork,
  useUpdateSpace,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { EntityImage } from '../common/components/EntityImage.js'
import { SystemSpacesIcons } from '../common/constants/icons.js'
import { fileSizeLimitError } from '../common/lib/upload.js'
import { useCropImageModal } from '../CropImageModal/useCropImageModal.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { PopoverImagePicker } from '../Picker/PopoverImagePicker.js'

const SpaceImageReadOnly = ({
  space,
  size = 'md',
  as,
}: {
  space: Space
  size: 'xs' | 'xsm' | 'sm' | 'md' | 'lg'
  as?: 'div' | 'span'
}) => {
  const defaultIcon = SystemSpacesIcons[space.slug]
  return (
    <EntityImage entity={space} size={size} defaultIcon={defaultIcon} as={as} />
  )
}

export const SpaceImage = ({
  space,
  size = 'md',
  readOnly = true,
  as,
}: {
  space: Space
  size?: 'xs' | 'xsm' | 'sm' | 'md' | 'lg'
  readOnly?: boolean
  as?: 'div' | 'span'
}) => {
  if (!space) {
    space = {} as Space
  }
  const { data: network } = useNetwork()
  const intl = useI18n()
  const { mutateAsync: createEmojis } = useCreateEmojis()
  const { mutateAsync: createImages } = useCreateImages()
  const { mutate: updateSpace } = useUpdateSpace()
  const [canUpdateSpace] = hasScopesPermission(space, ['updateSpace'])

  const uploadFile = useCallback(
    async (files: Array<File>) => {
      const args: UploadImagesArgs[] = [{ file: files[0] }]
      const uploadedImage = await createImages(args)
      if (uploadedImage?.[0]?.id) {
        updateSpace({
          id: space.id,
          input: {
            imageId: uploadedImage[0].id,
          },
        })
      }
    },
    [createImages, updateSpace, space],
  )

  const { onSelectFile, children: cropModal } = useCropImageModal({
    onSuccess: ({ croppedImage }) => {
      if (croppedImage.size > network?.settings?.imageSizeLimit) {
        toast({
          title: intl.$t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: fileSizeLimitError(
            network?.settings?.imageSizeLimit,
            'image',
            intl,
          ),
          status: 'error',
        })
        return
      }
      uploadFile([croppedImage])
    },
    options: {
      shape: 'rect',
      format: 'image/png',
    },
  })

  return canUpdateSpace && !readOnly ? (
    <>
      <PopoverImagePicker
        tabs={['emoji', 'upload', 'link']}
        sizeConfig={{
          recommendedWidth: 512,
          recommendedHeight: 512,
        }}
        onImageChange={files => onSelectFile(files?.[0])}
        onEmojiChange={async emoji => {
          const uploadedEmoji = await createEmojis([{ text: emoji?.id }])
          updateSpace({
            id: space.id,
            input: {
              imageId: uploadedEmoji[0].id || emoji.id,
            },
          })
        }}
      >
        <SpaceImageReadOnly size={size} space={space} as={as} />
      </PopoverImagePicker>
      {cropModal}
    </>
  ) : (
    <SpaceImageReadOnly size={size} space={space} as={as} />
  )
}
